import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Wrapper } from '../styled';
import Row from 'components/Row';
import { AutoColumn } from 'components/Column';
import { TYPE } from 'theme';
import { isMobile } from 'react-device-detect';
import user from 'stores/user';
import { Grid } from 'components/Grid';
import SocialCard from './Card';
import { BindResult, RemoveConfirm, RemoveResult } from './Modal';
import { useModal } from 'context/ModalContext';
import { socials, Social, Profile } from './config';
import useSocialAuth from './useSocialAuth';
import { useToast } from 'context/ToastContext';
import { authStore } from 'stores/authorizationStore';
import { SocialAuthCode, SocialAuthResult, SocialType } from 'constant/socialAuth';
import { toFormattedUnitsNum } from 'common-helpers/utils';
import { SocialProfileMission } from 'gql-ops/graph/__generated__/request';
// import { getOnePieceServerApi } from 'gql-ops';
import { GA_EVENT_ACTION, GA_EVENT_CATEGORY, trackEvent } from 'utils/googleAnalytics';

// const getSocialMission = (
//     missions: SocialProfileMission[],
//     socialType: SocialType,
// ): SocialProfileMission | undefined => {
//     // todo 目前暂时按name进行匹配 mission.name: 'LinkTwitter'
//     return missions?.find((item) => item.name.toLowerCase() === `link${socialType}`);
// };

const SocialProfile = () => {
    const toast = useToast();
    const [profiles, setProfiles] = useState<Record<string, Profile>>({});
    // const [missions, setMissions] = useState<SocialProfileMission[]>([]);
    // twitter授权成功，后端处理完成后回调到前端页面，带上code参数（?twitter_auth=2000｜2001），前端存入localstorage中。
    // 原始页面监听localstorage变化，根据code来处理页面UI的更新。
    const modal = useModal();
    const [triggerAuth, removeAuth] = useSocialAuth();

    const confirmBindResult = async (
        type: SocialType,
        bindResult: SocialAuthResult,
        profile: Profile,
        mission?: SocialProfileMission,
    ) => {
        // 用户已完成过任务或者当前连接的社交账号已完成过任务：弹窗信息里不显示bonus。
        const bonus = mission?.isCompleted ? undefined : toFormattedUnitsNum(mission?.point || '0');
        return modal.confirm({
            cancelButton: false,
            confirmText: 'OK',
            content: (
                <BindResult
                    code={bindResult?.code}
                    bindStats={bindResult?.stats}
                    social={socials[type] as Social}
                    profile={profile}
                    bonus={bonus}
                />
            ),
            shadowType: 'box',
            hideClose: false,
        });
    };
    const showRemoveResult = async (type: SocialType, code: SocialAuthCode) => {
        return modal.confirm({
            cancelButton: false,
            confirmText: 'OK',
            content: <RemoveResult code={code} social={socials[type] as Social} profile={profiles[type]} />,
            shadowType: 'box',
            hideClose: false,
        });
    };
    const connect = useCallback(
        async (type: SocialType) => {
            try {
                trackEvent({
                    category: GA_EVENT_CATEGORY.User,
                    action: GA_EVENT_ACTION.click_bind_social,
                    label: type,
                });
                try {
                    await authStore.getToken();
                } catch (error) {
                    // do nothing
                }
                const authResult = await triggerAuth(type);
                await refreshProfiles();
                const profile =
                    type === SocialType.Twitter
                        ? { ...user?.opsUser?.twitter, bindAddress: user.address }
                        : type === SocialType.Discord
                        ? { ...user?.opsUser?.discord, bindAddress: user.address }
                        : {};
                confirmBindResult(type, authResult, profile);
                // refreshMissions();
            } catch (error: any) {
                console.error(error);
                toast(error?.message || 'Oops, something went wrong.', 'warning');
            }
        },
        [triggerAuth, toast],
    );
    const disconnect = async (type: SocialType) => {
        const confirmed = await modal.confirm({
            content: <RemoveConfirm />,
            shadowType: 'box',
            hideClose: false,
        });
        if (confirmed) {
            const code = await removeAuth(type);
            showRemoveResult(type, code);
            refreshProfiles();
        }
    };

    const refreshProfiles = async () => {
        await user.refreshOpsUserInfo();
    };

    // const refreshMissions = async () => {
    //     try {
    //         const { userSocialProfileMissions } = await getOnePieceServerApi().userSocialProfileMissions({
    //             address: user.address,
    //         });
    //         setMissions(userSocialProfileMissions || []);
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    useEffect(() => {
        // 处理Twitter和Discord profile
        let twitterProfile: Profile = {};
        let discordProfile: Profile = {};

        if (user?.opsUser?.twitter) {
            twitterProfile = { ...user.opsUser.twitter, bindAddress: user.address };
        }

        if (user?.opsUser?.discord) {
            discordProfile = { ...user.opsUser.discord, bindAddress: user.address };
        }

        const newProfiles = {
            ...profiles,
            [SocialType.Twitter]: twitterProfile,
            [SocialType.Discord]: discordProfile,
        };
        setProfiles(newProfiles);
    }, [user.opsUser?.twitter, user.opsUser?.discord]);

    useEffect(() => {
        // 检查 URL 参数
        const params = new URLSearchParams(window.location.search);
        if (params.get('twitterAuthFailed') === 'true') {
            console.log('Twitter auth failed detected from URL, triggering reconnect');
            connect(SocialType.Twitter);
            // 清除 URL 参数
            const newUrl = window.location.pathname + '?scrollTo=socialProfile';
            window.history.replaceState({}, '', newUrl);
        }
    }, [connect]);

    return (
        <Wrapper id="socialProfile" style={{ scrollMarginTop: isMobile ? '100px' : 'unset' }}>
            <Row marginBottom="40px">
                <AutoColumn>
                    <TYPE.main fontSize={24} fontWeight={700} mobileFz={18} marginBottom={'6px'}>
                        Build Your Social Profile
                    </TYPE.main>
                    <TYPE.desc fontSize={14} color="#999">
                        link your social accounts to join daily social missions.
                    </TYPE.desc>
                    <TYPE.desc fontSize={14} color="#999">
                        <span style={{ color: '#F7931A' }}>Removal will be disabled during Season3 event.</span> Please
                        make sure you’re connecting the correct account before proceeding.
                    </TYPE.desc>
                </AutoColumn>
            </Row>
            <Grid col={1}>
                {Object.values(socials).map((social: any) => (
                    <SocialCard
                        key={social.type}
                        social={social}
                        // mission={getSocialMission(missions, social.type)}
                        profile={profiles[social.type]}
                        connect={connect}
                        disconnect={disconnect}
                    />
                ))}
            </Grid>
        </Wrapper>
    );
};

export default observer(SocialProfile);
